import { Toast } from "@base-ui/react/toast";

/**
 * A module-level toast manager — Base UI's own supported pattern for
 * showing a toast from outside a component (a TanStack Query mutation's
 * `onSuccess`/`onError`, a plain event handler, etc.), not just from
 * inside one via `useToastManager()`. Rendered by `<Toaster />`
 * (`components/ui/toast.tsx`, mounted once in `app/providers.tsx`); call
 * `toast.success()`/`toast.error()` below from anywhere.
 */
export const toastManager = Toast.createToastManager();

type ToastOptions = { description?: string };

export const toast = {
  success: (title: string, options?: ToastOptions) =>
    toastManager.add({ title, description: options?.description, type: "success", timeout: 5000 }),
  error: (title: string, options?: ToastOptions) =>
    toastManager.add({ title, description: options?.description, type: "error", timeout: 8000 }),
  info: (title: string, options?: ToastOptions) =>
    toastManager.add({ title, description: options?.description, type: "info", timeout: 5000 }),
};

/**
 * Supabase's `PostgrestError` is a plain `{ message, code, details, hint }`
 * object, not an `Error` subclass, so `String(error)` would render it as
 * "[object Object]". Read `.message` off any object that has one.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return typeof error === "string" ? error : "An unexpected error occurred. Please try again.";
}
